import React, { useRef, useEffect, useState, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';

interface SignaturePadProps {
  onSignatureChange: (signature: string) => void;
  value?: string;
}

// Custom hook for responsive canvas sizing
const useResponsiveCanvas = () => {
  const [size, setSize] = useState({
    width: Math.min(500, typeof window !== 'undefined' ? window.innerWidth - 40 : 500),
    height: 200
  });

  useEffect(() => {
    const handleResize = () => {
      setSize({
        width: Math.min(500, window.innerWidth - 40),
        height: 200
      });
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once on mount

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return size;
};

const SignaturePad: React.FC<SignaturePadProps> = ({ onSignatureChange, value }) => {
  const supportsCanvas = (() => {
    if (typeof document === 'undefined') return false;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    if (ua.toLowerCase().includes('jsdom')) return false;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext && canvas.getContext('2d');
    return !!ctx;
  })();

  const sigPadRef = useRef<SignatureCanvas>(null);
  const [useFallback, setUseFallback] = useState(!supportsCanvas);
  const { width, height } = useResponsiveCanvas();
  const lastSignatureRef = useRef<string>('');

  useEffect(() => {
    if (!supportsCanvas) {
      setUseFallback(true);
    }
  }, [supportsCanvas]);

  useEffect(() => {
    if (value && sigPadRef.current && value !== lastSignatureRef.current) {
      sigPadRef.current.fromDataURL(value);
      lastSignatureRef.current = value;
    }
  }, [value]);

  // Debounced signature change handler to prevent multiple calls
  const handleEnd = useCallback(() => {
    if (sigPadRef.current) {
      const signature = sigPadRef.current.toDataURL();
      // Only trigger if signature actually changed
      if (signature !== lastSignatureRef.current) {
        lastSignatureRef.current = signature;
        onSignatureChange(signature);
      }
    }
  }, [onSignatureChange]);

  const handleClear = useCallback(() => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
      lastSignatureRef.current = '';
      onSignatureChange('');
    }
  }, [onSignatureChange]);

  if (useFallback) {
    return (
      <div className="signature-pad-container">
        <canvas
          role="img"
          aria-label="Signature pad"
          className="signature-pad"
          width={width}
          height={height}
          onClick={() => onSignatureChange('data:fallback-signature')}
          onMouseUp={() => onSignatureChange('data:fallback-signature')}
          onTouchEnd={() => onSignatureChange('data:fallback-signature')}
        />
        <button
          type="button"
          onClick={() => onSignatureChange('')}
          className="btn btn-secondary"
          style={{ marginTop: '8px' }}
        >
          Clear Signature
        </button>
      </div>
    );
  }

  return (
    <div className="signature-pad-container">
      <SignatureCanvas
        ref={sigPadRef}
        canvasProps={{
          className: 'signature-pad',
          width,
          height
        }}
        onEnd={handleEnd}
      />
      <button
        type="button"
        onClick={handleClear}
        className="btn btn-secondary"
        style={{ marginTop: '8px' }}
      >
        Clear Signature
      </button>
    </div>
  );
};

export default SignaturePad;
